import { RRule } from "rrule";

export function useRRuleBuilder() {
	// Build RRule string from options
	const buildRRule = (frequency, options = {}) => {
		const rruleOptions = {
			freq: RRule[frequency],
			...options,
		};

		// Remove undefined values
		Object.keys(rruleOptions).forEach((key) => {
			if (rruleOptions[key] === undefined || rruleOptions[key] === null) {
				delete rruleOptions[key];
			}
		});

		try {
			const rule = new RRule(rruleOptions);
			return rule.toString();
		} catch (error) {
			console.error("Error building RRule:", error);
			return "";
		}
	};

	// German translations for RRule text
	const germanStrings = {
		every: "jede",
		until: "bis",
		day: "Tag",
		days: "Tage", 
		week: "Woche",
		weeks: "Wochen",
		on: "am",
		in: "in",
		"on the": "am",
		for: "für",
		and: "und",
		or: "oder",
		at: "um",
		last: "letzten",
		"(~ approximate)": "(~ ca.)",
		times: "mal",
		time: "Zeit",
		minutes: "Minuten",
		hours: "Stunden",
		weekdays: "Wochentage",
		weekday: "Wochentag",
		months: "Monate",
		month: "Monat",
		years: "Jahre",
		year: "Jahr",
	};

	// German day names
	const germanDayNames = [
		"Sonntag", "Montag", "Dienstag", "Mittwoch", 
		"Donnerstag", "Freitag", "Samstag"
	];

	// German month names
	const germanMonthNames = [
		"Januar", "Februar", "März", "April", "Mai", "Juni",
		"Juli", "August", "September", "Oktober", "November", "Dezember"
	];

	// Helper function to capitalize first letter
	const capitalize = (str) => {
		return str.charAt(0).toUpperCase() + str.slice(1);
	};

	// Format date in German locale
	const formatGermanDate = (year, month, day) => {
		const date = new Date(year, month, day);
		const formatter = new Intl.DateTimeFormat('de-DE', {
			day: 'numeric',
			month: 'long',
			year: 'numeric'
		});
		return formatter.format(date);
	};

	// Get human-readable description from RRule string
	const getHumanReadable = (rruleString) => {
		if (!rruleString) return "";

		try {
			const rule = RRule.fromString(rruleString);

			const language = {
				dayNames: germanDayNames,
				monthNames: germanMonthNames,
			};

			const getText = (id) => {
				return germanStrings[id] || id;
			};

			let germanText = rule.toText(getText, language, formatGermanDate);
			
			// Post-process to fix German grammar issues
			germanText = germanText
				.replace(/jede (\d+) Woche/g, 'alle $1 Wochen')
				.replace(/jede (\d+) Monat/g, 'alle $1 Monate')
				.replace(/jede (\d+) Jahr/g, 'alle $1 Jahre')
				.replace(/jede (\d+) Tag/g, 'alle $1 Tage')
				.replace(/jede Monat/g, 'jeden Monat')
				.replace(/jede Tag/g, 'jeden Tag')
				.replace(/jede Jahr/g, 'jedes Jahr')
				.replace(/(\d+)(st|nd|rd|th)/g, '$1.')
				.replace(/am (\d+\.), (\d+\.) und (\d+\.)/g, 'am $1, $2 und $3')
				.replace(/für (\d+) mal/g, '$1 mal')
				.replace(/für 1 Zeit/g, '1 mal')
				.replace(/für (\d+) Zeit/g, '$1 mal')
				.replace(/Monatee/g, 'Monate')
				.replace(/Tagee/g, 'Tage')
				.replace(/Jahree/g, 'Jahre')
				.replace(/Wochee/g, 'Wochen')
				.replace(/Wochenn/g, 'Wochen')
				.replace(/ (\d+\s*mal)/, ', insgesamt $1');

			return capitalize(germanText);
		} catch (error) {
			console.error("Error parsing RRule:", error);
			return "Ungültiges Muster";
		}
	};

	// Validate RRule string
	const validateRRule = (rruleString) => {
		if (!rruleString) return false;

		try {
			RRule.fromString(rruleString);
			return true;
		} catch (error) {
			return false;
		}
	};

	// Build daily patterns
	const buildDailyPattern = (dailyOptions) => {
		const { type, interval = 1 } = dailyOptions;

		switch (type) {
			case "daily":
				return buildRRule("DAILY", interval > 1 ? { interval } : {});
			case "weekdays":
				return buildRRule("WEEKLY", {
					byweekday: [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR],
				});
			default:
				return "";
		}
	};

	// Build weekly patterns
	const buildWeeklyPattern = (weeklyOptions) => {
		const { interval = 1, selectedDays = [] } = weeklyOptions;

		// Day mapping
		const dayMapping = {
			MO: RRule.MO,
			TU: RRule.TU,
			WE: RRule.WE,
			TH: RRule.TH,
			FR: RRule.FR,
			SA: RRule.SA,
			SU: RRule.SU,
		};

		const options = {};

		// Set interval if > 1
		if (interval > 1) {
			options.interval = interval;
		}

		// Set specific days if selected
		if (selectedDays.length > 0) {
			options.byweekday = selectedDays.map((day) => dayMapping[day]);
		}

		return buildRRule("WEEKLY", options);
	};

	// Build monthly patterns
	const buildMonthlyPattern = (monthlyOptions) => {
		const { type, interval = 1, selectedDays = [] } = monthlyOptions;

		const options = {};

		// Set interval if > 1
		if (interval > 1) {
			options.interval = interval;
		}

		switch (type) {
			case "monthly":
				// Just monthly with interval
				return buildRRule("MONTHLY", options);
			case "monthdays":
				// Specific days of month
				if (selectedDays.length > 0) {
					options.bymonthday = selectedDays;
				}
				return buildRRule("MONTHLY", options);
			case "lastday":
				// Last day of month
				options.bymonthday = -1;
				return buildRRule("MONTHLY", options);
			default:
				return "";
		}
	};

	// Parse RRule string back to options (for editing)
	const parseRRule = (rruleString) => {
		if (!rruleString) return null;

		try {
			const rule = RRule.fromString(rruleString);
			const options = rule.options;

			return {
				frequency: Object.keys(RRule).find(
					(key) => RRule[key] === options.freq
				),
				interval: options.interval || 1,
				byweekday: options.byweekday || [],
				bymonthday: options.bymonthday || [],
			};
		} catch (error) {
			console.error("Error parsing RRule:", error);
			return null;
		}
	};

	// Get every actual occurrence date for a pattern + start date (e.g. to correctly filter a
	// cancellations list against dates that genuinely belong to the pattern, rather than
	// naively trusting whatever's stored — a shrunk pattern can leave stale entries behind)
	const getOccurrenceDates = (startDate, pattern) => {
		try {
			// Ensure startDate is a Date object
			const dtstart = typeof startDate === 'string' ? new Date(startDate) : startDate;

			// Parse the pattern and manually set dtstart
			const cleanPattern = pattern.replace(/^RRULE:/, "");
			const rruleOptions = { dtstart };

			// Parse pattern manually
			cleanPattern.split(";").forEach((part) => {
				const [key, value] = part.split("=");
				if (key === "FREQ") {
					rruleOptions.freq = RRule[value];
				} else if (key === "COUNT") {
					rruleOptions.count = parseInt(value);
				} else if (key === "INTERVAL") {
					rruleOptions.interval = parseInt(value);
				} else if (key === "BYDAY") {
					rruleOptions.byweekday = value.split(",").map((day) => RRule[day]);
				} else if (key === "BYMONTHDAY") {
					rruleOptions.bymonthday = value.split(",").map((d) => parseInt(d));
				}
			});

			const rrule = new RRule(rruleOptions);

			return rrule.all();
		} catch (error) {
			console.error("Error generating occurrence dates:", error, { startDate, pattern });
			return [];
		}
	};

	const calculateEndDate = (startDate, pattern) => {
		const allOccurrences = getOccurrenceDates(startDate, pattern);

		return allOccurrences.length > 0 ? allOccurrences[allOccurrences.length - 1] : null;
	}

	return {
		buildRRule,
		getHumanReadable,
		validateRRule,
		buildDailyPattern,
		buildWeeklyPattern,
		buildMonthlyPattern,
		parseRRule,
		getOccurrenceDates,
		calculateEndDate,
	};
}
